import React, { useState, useEffect, useCallback } from "react";
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent, { TileStatusContent } from '../../../components/Tile/TileContent';
import RefreshButton from '../../../components/Tile/RefreshButton';
import { useGlobalAPI } from "../../../hooks/useGlobalAPI";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { useActivityTypes } from "./hooks/useActivityTypes";
import { getFileMetadata } from "../../visualization/LeadTimeHistogram/api/leadTimeHistogramApi";
import { useAutoFitFontSize } from "../_shared/useAutoFitFontSize";

const REOCCURRENCE_VALUE_MAX_PX = 72; // was text-7xl
const REOCCURRENCE_VALUE_MIN_PX = 20;

// Extracted so useAutoFitFontSize is called from a real component's render, not from the nested
// renderKPICard() helper — hooks called from a non-component helper violate the Rules of Hooks.
// No compact fallback: a median-hours value, not a raw large count.
const ReoccurrenceBigValue = ({ text }) => {
  const { ref, fontSizePx } = useAutoFitFontSize({
    text,
    maxPx: REOCCURRENCE_VALUE_MAX_PX,
    minPx: REOCCURRENCE_VALUE_MIN_PX,
  });

  return (
    <div ref={ref} className="max-w-full truncate font-bold mb-2 text-primary-500" style={{ fontSize: `${fontSizePx}px` }}>
      {text}
    </div>
  );
};

const ActivityReoccurrenceKPI = ({ activeFile, onRefresh, onGetHeaderActions }) => {
  // ✅ NEW: Use unified API hook - automatically applies global filters
  const api = useGlobalAPI(activeFile);

  // State for KPI data and UI
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Form state
  const [activityA, setActivityA] = useState('');
  const [activityB, setActivityB] = useState('');
  const [objectType, setObjectType] = useState('Auftrag');

  // Settings panel state
  const [isSettingsCollapsed, setIsSettingsCollapsed] = useState(() => {
    const saved = localStorage.getItem("activityReoccurrenceKPI_settingsCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Additional state for metadata
  const [fileMetadata, setFileMetadata] = useState(null);
  const [objectTypes, setObjectTypes] = useState([]);

  // Custom hooks for dropdown data
  const { activityTypes, loading: activityTypesLoading } = useActivityTypes(activeFile);

  // Save settings state to localStorage
  useEffect(() => {
    localStorage.setItem(
      "activityReoccurrenceKPI_settingsCollapsed",
      JSON.stringify(isSettingsCollapsed)
    );
  }, [isSettingsCollapsed]);


  // Fetch file metadata and object types when activeFile changes
  useEffect(() => {
    const fetchMetadata = async () => {
      if (!activeFile) {
        setObjectTypes([]);
        return;
      }

      try {
        console.log('🔄 Fetching metadata for ActivityReoccurrenceKPI:', activeFile);
        const metadata = await getFileMetadata(activeFile);
        setFileMetadata(metadata);
        
        if (metadata?.object_types) {
          const types = Object.keys(metadata.object_types);
          setObjectTypes(types);
          console.log('✅ Object types loaded for ActivityReoccurrenceKPI:', types);
        } else {
          // Fallback object types
          setObjectTypes(['Auftrag', 'Angebot', 'Lieferschein']);
        }
      } catch (error) {
        console.error('❌ Error fetching metadata for ActivityReoccurrenceKPI:', error);
        // Set fallback object types on error
        setObjectTypes(['Auftrag', 'Angebot', 'Lieferschein']);
      }
    };

    fetchMetadata();
  }, [activeFile]);

  // Auto-select first available activities if none selected
  useEffect(() => {
    if (activityTypes.length > 0) {
      if (!activityA && activityTypes.length >= 1) {
        setActivityA(activityTypes.find(a => a.includes('Auftrag anlegen')) || activityTypes[0]);
      }
      if (!activityB && activityTypes.length >= 2) {
        setActivityB(activityTypes.find(a => a.includes('Preisänderung')) || activityTypes[1]);
      }
    }
  }, [activityTypes, activityA, activityB]);

  // Auto-select first available object type if current is not available
  useEffect(() => {
    if (objectTypes.length > 0 && !objectTypes.includes(objectType)) {
      setObjectType(objectTypes[0]);
    }
  }, [objectTypes, objectType]);

  const toggleSettings = () => {
    setIsSettingsCollapsed((prev) => !prev);
  };

  const fetchKPIData = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return;
    }

    // Validate form before making request
    if (!activityA || !activityB || !objectType) {
      setError("Please select activities and object type");
      return;
    }

    if (activityA === activityB) {
      setError("Activity A and Activity B must be different");
      return;
    }

    setLoading(true);
    setError(null);
    setHasFetched(true);

    try {
      console.log("🔄 Fetching activity reoccurrence KPI data...", {
        activeFile,
        activityA,
        activityB,
        objectType,
      });

      // ✅ NEW: No manual filter building! Just pass component-specific params
      // Global filters (file_type, object_types, date_range, etc.) are automatically merged
      const response = await api.post('/activity-reoccurrence-kpi', {
        activity_a: activityA,
        activity_b: activityB,
        object_type: objectType,
      }, {}, 'Activity Reoccurrence KPI');
      
      console.log("📥 Activity Reoccurrence KPI response received:", response);
      setData(response);
    } catch (err) {
      console.error("❌ Error during KPI data fetch:", err);
      setError(err.message || "An unknown error occurred.");
    } finally {
      setLoading(false);
    }
  }, [activeFile, activityA, activityB, objectType, api]);

  // ✅ NEW: Auto-reload when global filters change
  useEffect(() => {
    if (hasFetched && !loading) {
      fetchKPIData();
    }
  }, [api.currentFilters, activeFile]);

  const hasData = !loading && !error && data !== null;
  const showInitialButton = !hasFetched && !loading;

  // Settings toggle button for header
  const settingsButton = (
    <button
      onClick={toggleSettings}
      className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
      aria-expanded={!isSettingsCollapsed}
      aria-label={isSettingsCollapsed ? "Show settings" : "Hide settings"}
    >
      <svg
        className={`w-3 h-3 transition-transform duration-150 ${
          isSettingsCollapsed ? "" : "rotate-180"
        }`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
      <span className="text-xs">{isSettingsCollapsed ? "Settings" : "Hide"}</span>
    </button>
  );

  // Auto-load data when persistent cache is enabled
  useAutoLoad(fetchKPIData, [activeFile, api.currentFilters, activityA, activityB, objectType], hasData);

  // Provide refresh function to parent (GridComponent)
  React.useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchKPIData);
    }
  }, [onRefresh, fetchKPIData]);

  // Provide header actions to parent (GridComponent)
  React.useEffect(() => {
    if (onGetHeaderActions) {
      onGetHeaderActions(settingsButton);
    }
  }, [onGetHeaderActions, settingsButton]);

  const isFormValid = () => {
    return activityA && 
           activityB && 
           objectType && 
           activityA !== activityB &&
           !activityTypesLoading && 
           objectTypes.length > 0;
  };

  const renderKPICard = () => {
    if (!data) return null;

    return (
      <div className="flex flex-row gap-4 flex-wrap w-full items-center justify-center">
        <div className="rounded-md p-4 flex-1 min-w-[200px] flex flex-col items-center justify-center text-center min-h-[120px] w-full">
          <ReoccurrenceBigValue
            text={data.median_time_hours !== null ? data.median_time_hours.toFixed(1) : '—'}
          />
          <div className="text-sm text-gray-500 text-center">
            Median Reoccurrence Time (hours)
          </div>
        </div>
      </div>
    );
  };


  return (
    <BaseTile
      loading={loading}
      error={error}
      minHeight="auto"
      padding="sm"
      className=""
    >
      <TileContent layout="flex" direction="column" grow>
        {/* Initial Load Button */}
        {showInitialButton && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={() => setIsSettingsCollapsed(false)}
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Configure KPI
            </button>
          </div>
        )}

        {/* Settings Panel */}
        {!isSettingsCollapsed && (
          <div className="overflow-hidden transition-all duration-200 ease-in-out mb-4">
            <div className="space-y-4 pb-4 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Activity A Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Activity A (Initial/Reoccurring)
                </label>
                <select
                  value={activityA}
                  onChange={(e) => setActivityA(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading || activityTypesLoading}
                >
                  <option value="">Select Activity A...</option>
                  {activityTypes.map((activity) => (
                    <option key={activity} value={activity}>
                      {activity}
                    </option>
                  ))}
                </select>
                {activityA && (
                  <p className="text-xs text-gray-500 mt-1">
                    This activity will be measured for reoccurrence
                  </p>
                )}
              </div>

              {/* Activity B Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Activity B (Intermediate)
                </label>
                <select
                  value={activityB}
                  onChange={(e) => setActivityB(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading || activityTypesLoading}
                >
                  <option value="">Select Activity B...</option>
                  {activityTypes.filter(activity => activity !== activityA).map((activity) => (
                    <option key={activity} value={activity}>
                      {activity}
                    </option>
                  ))}
                </select>
                {activityB && (
                  <p className="text-xs text-gray-500 mt-1">
                    This activity must occur between A's first and second occurrence
                  </p>
                )}
              </div>

              {/* Object Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Object Type
                </label>
                <select
                  value={objectType}
                  onChange={(e) => setObjectType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading || objectTypes.length === 0}
                >
                  {objectTypes.length > 0 ? (
                    objectTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))
                  ) : (
                    <option>Load KPI to see types</option>
                  )}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Analysis scope per individual object
                </p>
              </div>
            </div>

            {/* Validation Messages */}
            {activityA && activityB && activityA === activityB && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-amber-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-amber-700">
                    Activity A and Activity B must be different activities
                  </span>
                </div>
              </div>
            )}

            {/* Calculate KPI Button */}
            <div className="flex justify-center pt-2">
              <button
                onClick={fetchKPIData}
                disabled={!activeFile || !isFormValid() || loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading
                  ? "Calculating..."
                  : hasFetched
                  ? "Update KPI"
                  : "Calculate KPI"}
              </button>
            </div>
            </div>
          </div>
        )}

        {/* KPI Results */}
        {hasData && (
          <div className="pt-2">
            {renderKPICard()}
          </div>
        )}

        {/* No Data Message */}
        {hasFetched && !loading && !error && !hasData && (
          <TileStatusContent
            status="empty"
            title="No Sequences Found"
            message={`No objects were found with the sequence pattern: ${activityA} → ${activityB} → ${activityA}. Try selecting different activities or adjusting your filters.`}
            action={
              <button
                onClick={fetchKPIData}
                className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Retry
              </button>
            }
          />
        )}

        {/* Error State with Retry */}
        {error && hasFetched && (
          <div className="flex justify-center mt-4">
            <button
              onClick={fetchKPIData}
              className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Retry
            </button>
          </div>
        )}
      </TileContent>
    </BaseTile>
  );
};

export default ActivityReoccurrenceKPI;